import { IArtworkSprite } from "../../interfaces";
import _ from "lodash";
import {
  forceCenter,
  forceCollide,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from "d3-force";
import { Simulation } from "..";

export class CategoriesSimulation extends Simulation {
  private readonly artworks: any[];
  private readonly artworkSprites: IArtworkSprite[];
  public nodes: any[];
  public dimensions: { width: number; height: number };
  public onSimulationEnd?: (nodes: any[]) => void;
  private readonly nodePadding = 20;
  private simulationResults: {
    nodes: any[];
  } | null = null;
  public categories: string[] = [];
  public categoryCentroids: { [category: string]: { x: number; y: number } } =
    {};

  constructor(
    artworks: any[],
    artworkSprites: IArtworkSprite[],
    dimensions: { width: number; height: number },
  ) {
    super();
    this.nodes = [];
    this.artworks = artworks;
    this.artworkSprites = artworkSprites;
    this.dimensions = dimensions;
    this.setup();
  }

  protected setup(): void {
    this.nodes = this.generateClusterNodes();
    this.assignInitialCategoryCentroids();
  }

  /**
   * Generates nodes grouped by categories without any hierarchical structure.
   * @returns Array of node objects.
   */
  private generateClusterNodes(): any[] {
    return this.artworks.map((artwork) => {
      const sprite = _.find(
        this.artworkSprites,
        (artworkSprite) => artworkSprite.label === artwork.uuid,
      );
      return {
        name: artwork.name,
        uuid: artwork.uuid,
        category: artwork.category,
        sprite: sprite,
        x: this.dimensions.width / 2,
        y: this.dimensions.height / 2,
      };
    });
  }

  /**
   * Assigns initial center positions for each category to cluster nodes.
   * Ensures that category centroids do not overlap by maintaining a minimum distance between them.
   */
  private assignInitialCategoryCentroids(): void {
    const uniqueCategories = [...new Set(_.map(this.artworks, "category"))];
    const minDistance = 1000; // Minimum distance between category centroids (adjust as needed)
    const maxAttempts = 1000; // Maximum attempts to place a category centroid
    this.categories = uniqueCategories;

    uniqueCategories.forEach((category) => {
      const centroid = this.findNonOverlappingCentroid(
        minDistance,
        maxAttempts,
      );
      this.categoryCentroids[category] = centroid;
    });

    console.debug(
      `Category centroids assigned (Non-overlapping Layout):`,
      this.categoryCentroids,
    );
  }

  /**
   * Find a centroid position that doesn't overlap with existing centroids.
   * @param minDistance Minimum distance between centroids
   * @param maxAttempts Maximum number of placement attempts
   * @returns Centroid coordinates
   */
  private findNonOverlappingCentroid(
    minDistance: number,
    maxAttempts: number,
  ): { x: number; y: number } {
    let attempts = 0;
    let placed = false;
    let finalX = 0;
    let finalY = 0;

    while (!placed && attempts < maxAttempts) {
      attempts++;
      // Base random position
      let baseX = Math.random() * this.dimensions.width;
      let baseY = Math.random() * this.dimensions.height;

      // Apply Perlin noise for smoother variations
      const noiseScale = 0.98; // Adjust for more or less variation
      const baseXScale = baseX * noiseScale;
      const baseYScale = baseY * noiseScale;
      const noiseX = this.noiseGenerator.perlin2(baseXScale, baseYScale);
      const noiseY = this.noiseGenerator.perlin2(baseXScale, baseYScale);

      // Adjust positions with noise
      finalX = baseX + noiseX * 50; // Scale noise effect as needed
      finalY = baseY + noiseY * 50;

      // Ensure the centroids stay within bounds
      finalX = Math.max(0, Math.min(this.dimensions.width, finalX));
      finalY = Math.max(0, Math.min(this.dimensions.height, finalY));

      // Check distance from existing centroids
      const tooClose = Object.values(this.categoryCentroids).some(
        (centroid) => {
          const dx = centroid.x - finalX;
          const dy = centroid.y - finalY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          return distance < minDistance;
        },
      );

      if (!tooClose) {
        placed = true;
      }
    }

    if (!placed) {
      console.warn(
        `Could not place category with minimum distance after ${maxAttempts} attempts. Placing it anyway.`,
      );
    }

    return { x: finalX, y: finalY };
  }

  /**
   * Creates simulation forces for category-based clustering.
   * @param nodes Nodes for the simulation.
   * @returns D3 force simulation.
   */
  private createSimulationForces(nodes: any[]) {
    return forceSimulation(nodes)
      .force(
        "charge",
        forceManyBody().strength(-30), // Gentle repulsion
      )
      .force(
        "collision",
        forceCollide()
          .radius((d: any) => {
            const scaledRadius = d.sprite.radius * (d.sprite.scaleFactor || 1);
            return scaledRadius + this.nodePadding;
          })
          .strength(1.0),
      )
      .force(
        "x",
        forceX((d: any) => this.categoryCentroids[d.category].x).strength(1.0),
      )
      .force(
        "y",
        forceY((d: any) => this.categoryCentroids[d.category].y).strength(1.0),
      )
      .force(
        "center",
        forceCenter(this.dimensions.width / 2, this.dimensions.height / 2),
      );
  }

  /**
   * Creates and configures a simulation with standard event handlers.
   * @param nodes Nodes for the simulation
   * @param tickCallback Optional callback for tick events
   * @param endCallback Optional callback for end events
   * @returns Configured simulation instance
   */
  private createConfiguredSimulation(
    nodes: any[],
    tickCallback?: () => void,
    endCallback?: () => void,
  ) {
    const simulation = this.createSimulationForces(nodes);

    // Add tick event to update sprites in real-time
    simulation.on("tick", () => {
      this.updateSprites();
      if (tickCallback) tickCallback();
    });

    // Add end event to ensure final sprite positions are set
    simulation.on("end", () => {
      if (endCallback) endCallback();
      this.updateSprites();
    });

    return simulation;
  }

  /**
   * Calculates the centroid (average position) for each category after the simulation.
   * @returns An object with category names as keys and their centroids as values.
   */
  public calculateCategoryCentroids(): {
    [category: string]: { x: number; y: number };
  } {
    if (!this.simulationResults) {
      console.warn("No simulation results available to calculate centroids");
      return {};
    }

    const categoryCentroids: { [category: string]: { x: number; y: number } } =
      {};

    // Group nodes by category
    const nodesByCategory = _.groupBy(this.simulationResults.nodes, "category");

    // Calculate centroid for each category
    Object.entries(nodesByCategory).forEach(([category, categoryNodes]) => {
      // Calculate average x and y
      const centroid = categoryNodes.reduce(
        (acc, node) => {
          acc.x += node.x;
          acc.y += node.y;
          return acc;
        },
        { x: 0, y: 0 },
      );

      centroid.x /= categoryNodes.length;
      centroid.y /= categoryNodes.length;

      categoryCentroids[category] = centroid;
    });

    console.debug("Category Centroids:", categoryCentroids);
    return categoryCentroids;
  }

  /**
   * Runs the simulation.
   */
  public run(): void {
    console.info("Starting simulation...");
    console.time("Simulation Time");

    const simulation = this.createConfiguredSimulation(
      this.nodes,
      undefined,
      () => {
        // Store simulation results
        this.simulationResults = {
          nodes: this.nodes,
        };

        // Calculate and store category centroids
        this.categoryCentroids = this.calculateCategoryCentroids();
        console.log(this.categoryCentroids);

        // Record end time and log duration
        console.timeEnd("Simulation Time");

        this.instance = simulation;
        // Notify callback
        if (this.onSimulationEnd) {
          this.onSimulationEnd(this.nodes);
        }
      },
    );

    console.info("Simulation running...");
  }

  /**
   * Updates sprite positions based on current node positions.
   */
  private updateSprites(): void {
    this.nodes.forEach((node: any) => {
      if (node.sprite) {
        node.sprite.x = node.x;
        node.sprite.y = node.y;
      }
    });
  }

  /**
   * Resets the simulation with animation.
   * @param events Callbacks for tick and end events.
   */
  public resetSimulation(events: {
    onTick: () => void;
    onTickEnd: () => void;
  }): void {
    // If no previous simulation results, run the initial simulation
    if (!this.simulationResults) {
      return;
    }

    this.instance.stop();

    this.simulationResults.nodes.forEach((node) => {
      if (node.sprite) {
        node.sprite.visible = true;
      }
    });

    // Restore previous simulation state
    this.nodes = this.simulationResults.nodes;

    // Recreate simulation with animation
    this.instance = this.createConfiguredSimulation(
      this.nodes,
      events.onTick,
      events.onTickEnd,
    );
  }

  /**
   * Filters nodes based on categories in real-time.
   * @param categories Array of categories to keep.
   */
  public filterNodesByCategories(categories: string[]): void {
    if (!this.simulationResults) {
      console.warn("No previous simulation results to filter");
      return;
    }

    this.instance.stop();

    // Filter nodes based on categories
    const filteredNodes = this.simulationResults.nodes.filter((node) =>
      categories.includes(node.category),
    );

    // Hide sprites for nodes not in the filtered set
    this.simulationResults.nodes.forEach((node) => {
      if (node.sprite) {
        node.sprite.visible = filteredNodes.includes(node);
      }
    });

    // Update current state
    this.nodes = filteredNodes;

    // Recreate simulation with filtered nodes
    this.instance = this.createConfiguredSimulation(this.nodes);
  }
}
